import { Avatar, Text } from "@certn/cascadia-react";
import { SkeletonGrid } from "@certn/cascadia-react/patterns/skeleton-grid/SkeletonGrid";
import { formatDate, parseDate, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { startCase } from "lodash";
import { useTranslation } from "react-i18next";

import { DismissTaskButton } from "./DismissTasksButton";
import { TaskListRow } from "./TaskListRow";
import { iconColorFromType, iconFromType } from "./utils";
import { useUser } from "../../hooks/useUser";
import { Schemas } from "../../types";
interface Props {
  task: Schemas["LoggedInUserTaskList"];
  onRowPress: (task: Schemas["LoggedInUserTaskList"]) => void;
  mutate?: any;
  isLoading?: boolean;
}

export function TaskRow(props: Props) {
  const { task, mutate, onRowPress, isLoading } = props;
  const { t } = useTranslation();
  const { details: userDetails } = useUser();

  function getActionButton(item: any) {
    const isDisabled =
      item?.assigned_to?.auth0_id !== userDetails?.auth0_id ||
      !item?.is_dismissable_by_client;
    return (
      <DismissTaskButton item={item} mutate={mutate} isDisabled={isDisabled} />
    );
  }

  function completedDismissed(item: any): React.ReactNode {
    const firstWord = item.is_dismissed
      ? t("Dismissed on ")
      : t("Completed on ");
    return firstWord + formatDate(item.completed_at, "MMM dd, yyyy");
  }

  if (isLoading) {
    return (
      <div>
        <SkeletonGrid columns={1} rows={1} />
      </div>
    );
  }
  return (
    <>
      {task.status === "ACTIVE" ? (
        <TaskListRow
          icon={iconFromType(task.type)}
          color={iconColorFromType(task.type)}
          type={startCase(task.type.toLowerCase())}
          description={task.description}
          date={parseDate(task.created)}
          onRowPress={() => {
            onRowPress(task);
          }}
          actions={
            <div className={css(styles.avatarButtonGroup)}>
              <Avatar
                size="small"
                name={parseName(
                  task?.assigned_to?.auth0_full_name || undefined
                )}
              />
              {getActionButton(task)}
            </div>
          }
        />
      ) : (
        <TaskListRow
          icon="check"
          color="neutral"
          type={startCase(task.type.toLowerCase())}
          description={task.description}
          date={parseDate(task.created)}
          onRowPress={() => {
            onRowPress(task);
          }}
          actions={
            <div className={css(styles.avatarButtonGroup)}>
              <Avatar
                size="small"
                name={parseName(
                  task?.assigned_to?.auth0_full_name || undefined
                )}
              />
              <Text
                className={css(styles.completedDismissed)}
                style="global/info-label"
              >
                {completedDismissed(task)}
              </Text>
            </div>
          }
        />
      )}
    </>
  );
}

const styles = {
  avatarButtonGroup: {
    display: "flex",
    gap: "var(--space-3)",
    alignItems: "center",
  },
  completedDismissed: {
    color: "var(--text-weak)",
  },
};
